import {CheckCircleIcon, XCircleIcon} from "@heroicons/react/24/outline";
import {assetIssueLabel, bookingIssueLabel, formatCents} from "../format.ts";
import {AssetPreview, BookingPreview, ImportPreview} from "../types.ts";

interface ImportPreviewTablesProps {
    preview: ImportPreview;
}

export function ImportPreviewTables({preview}: ImportPreviewTablesProps) {
    return (
        <div className="space-y-8">
            <BookingsTable bookings={preview.bookings}/>
            {preview.assets.length > 0 && <AssetsTable assets={preview.assets}/>}
        </div>
    );
}

function BookingsTable({bookings}: {bookings: BookingPreview[]}) {
    return (
        <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Memoriaalboekingen ({bookings.length})
            </h2>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-gray-500">
                        <tr>
                            <th className="px-4 py-2 font-medium">Status</th>
                            <th className="px-4 py-2 font-medium">Datum</th>
                            <th className="px-4 py-2 font-medium">Omschrijving</th>
                            <th className="px-4 py-2 font-medium text-right">Regels</th>
                            <th className="px-4 py-2 font-medium text-right">Debet</th>
                            <th className="px-4 py-2 font-medium text-right">Credit</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {bookings.map((booking) => (
                            <tr key={booking.index} className={booking.blocking ? "bg-red-50" : ""}>
                                <td className="px-4 py-2">
                                    <StatusIcon blocking={booking.blocking}/>
                                </td>
                                <td className="px-4 py-2 whitespace-nowrap">{booking.bookingDate}</td>
                                <td className="px-4 py-2">
                                    <div>{booking.auditReason || "—"}</div>
                                    <IssueList
                                        details={[
                                            ...booking.issues.map(bookingIssueLabel),
                                            ...(booking.errorDetail ? [booking.errorDetail] : []),
                                        ]}
                                    />
                                </td>
                                <td className="px-4 py-2 text-right">{booking.lineCount}</td>
                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                    {formatCents(booking.totalDebitCents)}
                                </td>
                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                    {formatCents(booking.totalCreditCents)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function AssetsTable({assets}: {assets: AssetPreview[]}) {
    return (
        <section>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">
                Bedrijfsmiddelen ({assets.length})
            </h2>
            <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 text-left text-gray-500">
                        <tr>
                            <th className="px-4 py-2 font-medium">Status</th>
                            <th className="px-4 py-2 font-medium">Naam</th>
                            <th className="px-4 py-2 font-medium text-right">Aanschafwaarde</th>
                            <th className="px-4 py-2 font-medium text-right">Restwaarde</th>
                            <th className="px-4 py-2 font-medium text-right">Levensduur (mnd)</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {assets.map((asset) => (
                            <tr key={asset.index} className={asset.blocking ? "bg-red-50" : ""}>
                                <td className="px-4 py-2">
                                    <StatusIcon blocking={asset.blocking}/>
                                </td>
                                <td className="px-4 py-2">
                                    <div>{asset.name || "—"}</div>
                                    <IssueList
                                        details={[
                                            ...asset.issues.map(assetIssueLabel),
                                            ...(asset.errorDetail ? [asset.errorDetail] : []),
                                        ]}
                                    />
                                </td>
                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                    {formatCents(asset.purchaseValueCents)}
                                </td>
                                <td className="px-4 py-2 text-right whitespace-nowrap">
                                    {formatCents(asset.residualValueCents)}
                                </td>
                                <td className="px-4 py-2 text-right">
                                    {asset.economicLifetimeMonths}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

function StatusIcon({blocking}: {blocking: boolean}) {
    if (blocking) return <XCircleIcon className="h-5 w-5 text-red-500"/>;
    return <CheckCircleIcon className="h-5 w-5 text-green-500"/>;
}

function IssueList({details}: {details: string[]}) {
    if (details.length === 0) return null;
    return (
        <ul className="mt-1 text-xs text-red-600 list-disc list-inside">
            {details.map((detail) => (
                <li key={detail}>{detail}</li>
            ))}
        </ul>
    );
}
